import { useEffect, useState } from "react";
import { BellRing, CheckCircle2, Download, Send, Share, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { currentSubscription, pushSupported, subscribePush, useInstallPrompt } from "@/lib/pwa";
import { cn } from "@/lib/utils";

function Shell({ title, icon: Icon, children, testId }) {
  return (
    <div className="gt-card p-5" data-testid={testId}>
      <div className="mb-4 flex items-center gap-2 text-sm font-bold"><Icon className="h-4 w-4 gt-brand-text" /> {title}</div>
      {children}
    </div>
  );
}

const Step = ({ n, children }) => (
  <li className="flex gap-3 text-sm"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 font-mono text-xs font-bold gt-brand-text">{n}</span><span className="pt-0.5">{children}</span></li>
);

function InstallSteps({ ios }) {
  return (
    <ol className="space-y-2.5" data-testid="install-steps">
      {ios ? (<>
        <Step n={1}>Buka alamat aplikasi ini di <b>Safari</b>.</Step>
        <Step n={2}>Ketuk tombol <b>Bagikan</b> <Share className="inline h-3.5 w-3.5" /> di bawah layar.</Step>
        <Step n={3}>Pilih <b>Tambah ke Layar Utama</b>, lalu ketuk <b>Tambah</b>.</Step>
      </>) : (<>
        <Step n={1}>Buka alamat aplikasi ini di <b>Chrome</b> (Android) atau Chrome/Edge (komputer).</Step>
        <Step n={2}>Ketuk menu <b>⋮</b> lalu pilih <b>Instal aplikasi</b> / <b>Tambahkan ke layar utama</b>.</Step>
        <Step n={3}>Ikon GLAZY muncul di layar HP dan terbuka layar penuh seperti aplikasi biasa.</Step>
      </>)}
    </ol>
  );
}

export function InstallAppCard() {
  const { canPrompt, installed, ios, prompt } = useInstallPrompt();
  const install = async () => { if (await prompt()) toast.success("Aplikasi GLAZY TRACKER terpasang"); };
  return (
    <Shell title="Aplikasi HP" icon={Smartphone} testId="settings-install-card">
      {installed ? (
        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-500" data-testid="install-status-installed"><CheckCircle2 className="h-4 w-4" /> Aplikasi sudah terpasang di perangkat ini.</div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Pasang GLAZY TRACKER di layar utama HP: ikon sendiri, layar penuh, dan bisa menerima notifikasi alarm. Tanpa Play Store / App Store.</p>
          {canPrompt && <button onClick={install} className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground transition-transform active:scale-95" data-testid="install-app-button"><Download className="h-4 w-4" /> Pasang Aplikasi</button>}
          <InstallSteps ios={ios} />
        </div>
      )}
    </Shell>
  );
}

const LEVELS = [["CRITICAL", "Hanya kritis", "Bergerak tanpa ACC, power terputus"], ["WARNING", "Peringatan & kritis", "Ditambah overspeed, keluar geofence, offline, baterai"]];

function LevelPicker({ value, onChange, disabled }) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {LEVELS.map(([k, l, d]) => (
        <button key={k} disabled={disabled} onClick={() => onChange(k)} data-testid={`push-level-${k.toLowerCase()}`}
          className={cn("rounded-xl border p-3 text-left transition-colors disabled:opacity-50", value === k ? "border-primary bg-primary/10" : "border-border hover:border-primary/40")}>
          <div className="text-sm font-bold">{l}</div><div className="text-xs text-muted-foreground">{d}</div>
        </button>
      ))}
    </div>
  );
}

export function PushCard() {
  const { installed, ios } = useInstallPrompt();
  const supported = pushSupported();
  const [cfg, setCfg] = useState(null);
  const [sub, setSub] = useState(null);
  const [level, setLevel] = useState("CRITICAL");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/push/config").then(async ({ data }) => {
      setCfg(data);
      const s = supported ? await currentSubscription() : null;
      const mine = s && data.subscriptions.find((x) => x.endpoint === s.endpoint);
      setSub(mine ? s : null);
      if (mine) setLevel(mine.min_priority);
    }).catch(() => setCfg({ subscriptions: [] }));
  }, [supported]);

  const save = async (s, lv) => {
    const j = s.toJSON();
    await api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys, min_priority: lv });
  };
  const toggle = async (on) => {
    setBusy(true);
    try {
      if (on) {
        const s = await subscribePush(cfg.public_key);
        await save(s, level);
        setSub(s);
        toast.success("Notifikasi HP aktif");
      } else {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
        setSub(null);
        toast.success("Notifikasi HP dimatikan");
      }
    } catch (e) { toast.error(e?.response ? errMsg(e) : e.message); }
    setBusy(false);
  };
  const changeLevel = async (lv) => {
    setLevel(lv);
    if (sub) { try { await save(sub, lv); toast.success("Jenis alarm diperbarui"); } catch (e) { toast.error(errMsg(e)); } }
  };
  const test = async () => {
    try { const { data } = await api.post("/push/test"); toast.success(`Notifikasi tes dikirim ke ${data.sent} perangkat`); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!cfg) return null;
  const blocked = supported && Notification.permission === "denied";
  return (
    <Shell title="Notifikasi HP" icon={BellRing} testId="settings-push-card">
      {!supported ? (
        <p className="text-sm text-muted-foreground" data-testid="push-unsupported">{ios && !installed ? "Di iPhone, pasang aplikasi ke Layar Utama dulu (iOS 16.4 ke atas), lalu buka dari ikon GLAZY dan aktifkan di sini." : "Browser ini belum mendukung notifikasi. Gunakan Chrome di Android atau aplikasi yang sudah dipasang."}</p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-semibold" data-testid="push-status">{sub ? "Aktif di perangkat ini" : "Belum aktif di perangkat ini"}</div>
              <div className="text-xs text-muted-foreground">Alarm kendaraan muncul di HP walau aplikasi sedang ditutup.</div>
            </div>
            <Switch checked={!!sub} disabled={busy || blocked} onCheckedChange={toggle} data-testid="push-toggle" />
          </div>
          {blocked && <p className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-400" data-testid="push-blocked">Notifikasi diblokir. Izinkan notifikasi untuk situs ini di pengaturan browser/HP, lalu muat ulang halaman.</p>}
          <LevelPicker value={level} onChange={changeLevel} disabled={busy} />
          {sub && <button onClick={test} className="flex items-center gap-1.5 rounded-xl border border-border px-3 py-2 text-xs font-bold hover:border-primary/50" data-testid="push-test-button"><Send className="h-3.5 w-3.5" /> Kirim notifikasi tes</button>}
        </div>
      )}
    </Shell>
  );
}

export function InstallButton() {
  const { canPrompt, installed, prompt } = useInstallPrompt();
  if (!canPrompt || installed) return null;
  return (
    <button onClick={prompt} className="flex h-10 items-center gap-1.5 rounded-full bg-primary px-3 text-xs font-bold text-primary-foreground transition-transform active:scale-95" data-testid="topbar-install-button">
      <Download className="h-4 w-4" /> <span className="hidden sm:inline">Pasang</span>
    </button>
  );
}
